import { Plus, X } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { Pesticide } from "../../../../../types/prodcut-data-review/product-data.types";

interface PesticideRowFormValues {
  id: string;
  pesticideQuantity: string;
}

interface PesticideRowProps {
  pesticide: Pesticide;
  pesticideList: any[];
  editing: boolean;
  lookupText: (list: any[], id: number | string) => string;
  onUpdate: (id: number, patch: Partial<Pesticide>) => void;
  onRemove: (id: number) => void;
}

const PesticideRow: React.FC<PesticideRowProps> = ({
  pesticide,
  pesticideList,
  editing,
  lookupText,
  onUpdate,
  onRemove
}) => {
  const form = useForm<PesticideRowFormValues>({
    defaultValues: { id: String(pesticide.id), pesticideQuantity: pesticide.pesticideQuantity }
  });

  useEffect(() => {
    const subscription = form.watch(data => {
      onUpdate(pesticide.id, { id: Number(data.id), pesticideQuantity: data.pesticideQuantity });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Form {...form}>
      <TableRow>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="id"
              label=""
              placeholder="Select pesticide"
              options={pesticideList}
            />
          ) : (
            <span className="tw-text-sm">{lookupText(pesticideList, pesticide.id) || "—"}</span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormInputNumber control={form.control} name="pesticideQuantity" label="" />
          ) : (
            <span className="tw-text-sm tw-font-mono">
              {pesticide.pesticideQuantity !== "" ? pesticide.pesticideQuantity : "—"}
            </span>
          )}
        </TableCell>
        <TableCell className="tw-text-right">
          {editing && (
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={() => onRemove(pesticide.id)}
              title="Remove"
            >
              <X className="tw-h-3.5 tw-w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    </Form>
  );
};

interface PesticidesSectionProps {
  pesticides: Pesticide[];
  pesticideList: any[];
  editing: boolean;
  subSectionHeader: string;
  lookupText: (list: any[], id: number | string) => string;
  onAdd: () => void;
  onUpdate: (id: number, patch: Partial<Pesticide>) => void;
  onRemove: (id: number) => void;
}

const PesticidesSection: React.FC<PesticidesSectionProps> = ({
  pesticides,
  pesticideList,
  editing,
  subSectionHeader,
  lookupText,
  onAdd,
  onUpdate,
  onRemove
}) => {
  return (
    <div className="tw-border-t">
      <div className={subSectionHeader}>
        <span className="tw-text-xs tw-font-semibold tw-text-primary tw-uppercase tw-tracking-wide">
          Pesticides
        </span>
        {editing && (
          <Button variant="outline" size="sm" className="tw-h-7 tw-text-xs" onClick={onAdd}>
            <Plus className="tw-h-3 tw-w-3 tw-mr-1" />
            Add pesticide
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw-text-xs">Pesticide Name</TableHead>
            <TableHead className="tw-text-xs tw-w-[200px]">Amount (kg/ha)</TableHead>
            <TableHead className="tw-text-xs tw-w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pesticides.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="tw-text-xs tw-text-muted-foreground tw-italic">
                No pesticides.
              </TableCell>
            </TableRow>
          )}
          {pesticides.map(p => (
            <PesticideRow
              key={p.id}
              pesticide={p}
              pesticideList={pesticideList}
              editing={editing}
              lookupText={lookupText}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PesticidesSection;
